import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { StatTile } from '@/components/StatTile'
import { fmtDate, fmtNumber, useT } from '@/i18n'
import { inventoryMessages } from '@/i18n/messages/inventory'
import type { ReconciliationView, ReconStatus } from './types'

/**
 * Where the cable went (inventory phase 3): per project and cable, what came off the drums
 * against what the crews reported laying, and the same per crew. Read from the drum ledger and
 * the Aufmaß reports; nothing here is entered by hand.
 */
export function ReconciliationPage() {
  const t = useT(inventoryMessages)
  const { data, isPending } = useQuery<ReconciliationView>({
    queryKey: ['inv-recon'], queryFn: () => api.get<ReconciliationView>('/api/inventory/reconciliation'),
  })
  const loader = usePageLoader(isPending, t('reconLoading'))
  if (loader) return loader
  const cables = (data?.projects ?? []).flatMap((p) => p.cables)
  const used = cables.reduce((s, c) => s + c.usedM, 0)
  const laid = cables.reduce((s, c) => s + c.laidM, 0)

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/inventory" className="text-xs text-neutral-500 hover:underline">← {t('title')}</Link>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{t('reconTitle')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-neutral-500">{t('reconSubtitle')}</p>
        </div>
        <a href="/api/inventory/reconciliation.csv"
          className="inline-flex min-h-11 items-center rounded-xl px-4 text-sm font-medium text-neutral-700 ring-1 ring-neutral-300 hover:bg-neutral-50 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-neutral-800">
          ⤓ {t('reconExport')}
        </a>
      </header>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <StatTile label={t('reconTileUsed')} value={used} unit="m" />
        <StatTile label={t('reconTileLaid')} value={laid} unit="m" />
        <StatTile label={t('reconTileUnaccounted')} value={used - laid} unit="m" />
        <StatTile label={t('reconTileFlagged')} value={cables.filter((c) => c.status !== 'ok').length} unit="" />
      </div>
      <p className="max-w-3xl text-xs text-neutral-500">
        {t('reconHow', { tol: fmtNumber(data?.toleranceM ?? 50), pct: fmtNumber((data?.tolerancePct ?? 0.05) * 100) })}
      </p>

      {(data?.projects.length ?? 0) === 0 && (
        <p className="rounded-2xl bg-neutral-50 p-6 text-center text-sm text-neutral-500 dark:bg-neutral-900">{t('reconEmpty')}</p>
      )}

      {data?.projects.map((p) => (
        <section key={p.projectId} className="space-y-2">
          <h2 className="text-lg font-semibold">
            <Link to={`/projects/${p.projectId}`} className="hover:underline">{p.projectName ?? '—'}</Link>
            {p.codeInternal && <span className="ml-2 text-sm font-normal text-neutral-500">{p.codeInternal}</span>}
          </h2>
          <div className="space-y-2">
            {p.cables.map((c) => (
              <article key={c.cableName} className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">{c.cableName}</div>
                    <div className="text-xs text-neutral-500">
                      {t('drumsCount', { count: c.drums })} · {t('since', { date: fmtDate(c.trackedSince) })}
                      {c.hasEstimates && <span className="text-amber-700 dark:text-amber-300"> · {t('hasEstimates')}</span>}
                    </div>
                  </div>
                  <StatusPill status={c.status} />
                </div>
                <Flow c={c} />
                {c.shortM != null && (
                  <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
                    ⚠ {t('shortWarn', { metres: fmtNumber(c.shortM) })}
                  </p>
                )}
                {c.status !== 'ok' && <p className="mt-2 text-xs text-neutral-500">{c.status === 'missing' ? t('hintMissing') : t('hintOver')}</p>}
              </article>
            ))}
          </div>
        </section>
      ))}

      {(data?.crews.length ?? 0) > 0 && (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">{t('crewsTitle')}</h2>
          <p className="text-xs text-neutral-500">{t('crewsHint')}</p>
          <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
                  <th className="px-3 py-2 font-medium">{t('crew')}</th>
                  <th className="px-3 py-2 font-medium">{t('cable')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('colUsed')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('colLaid')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('colUnaccounted')}</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {data!.crews.map(({ recon: c, projectName }) => (
                  <tr key={`${c.kolonaId}-${c.projectId}-${c.cableName}`} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
                    <td className="px-3 py-2"><div className="font-medium">{c.crew ?? '—'}</div><div className="text-xs text-neutral-500">{projectName}</div></td>
                    <td className="min-w-[12rem] px-3 py-2 text-xs">{c.cableName}</td>
                    <td className="tabular px-3 py-2 text-right">{fmtNumber(c.usedM)} m</td>
                    <td className="tabular px-3 py-2 text-right">{fmtNumber(c.laidM)} m</td>
                    <td className="tabular px-3 py-2 text-right font-semibold">{signed(c.unaccountedM)} m</td>
                    <td className="px-3 py-2"><StatusPill status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}

const signed = (v: number) => (v > 0 ? `+${fmtNumber(v)}` : fmtNumber(v))

export function StatusPill({ status }: { status: ReconStatus }) {
  const t = useT(inventoryMessages)
  const [label, tone] = {
    ok: [t('statusOk'), 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300'],
    missing: [t('statusMissing'), 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300'],
    over: [t('statusOver'), 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300'],
  }[status]
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ${tone}`}>{status === 'ok' ? '✓' : '⚠'} {label}</span>
}

/** The arithmetic, left to right, so anyone can check it. */
function Flow({ c }: { c: ReconciliationView['projects'][number]['cables'][number] }) {
  const t = useT(inventoryMessages)
  const cell = (label: string, v: number, op?: string, strong = false) => (
    <div className="flex items-baseline gap-1.5">
      {op && <span className="text-neutral-400">{op}</span>}
      <div>
        <div className="text-[11px] text-neutral-500">{label}</div>
        <div className={`tabular ${strong ? 'text-base font-semibold' : 'text-sm'}`}>{fmtNumber(v)} m</div>
      </div>
    </div>
  )
  return (
    <div className="mt-3 flex flex-wrap items-end gap-x-4 gap-y-2">
      {cell(t('colSent'), c.sentM)}
      {cell(t('colReturned'), c.returnedM, '−')}
      {c.writtenOffM > 0 && cell(t('colWrittenOff'), c.writtenOffM, '−')}
      {cell(t('colOnSite'), c.onSiteM, '−')}
      {cell(t('colUsed'), c.usedM, '=', true)}
      {cell(t('colLaid'), c.laidM, '−')}
      <div className="flex items-baseline gap-1.5">
        <span className="text-neutral-400">=</span>
        <div>
          <div className="text-[11px] text-neutral-500">{t('colUnaccounted')}</div>
          <div className={`tabular text-base font-bold ${c.status === 'missing' ? 'text-red-700 dark:text-red-300' : c.status === 'over' ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'}`}>
            {signed(c.unaccountedM)} m
          </div>
        </div>
      </div>
    </div>
  )
}
